import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { notifyAdminOfDossierSubmission } from "@/lib/notifications";
import {
  FileEdit, Send, CheckCircle2, BadgeCheck, AlertTriangle, Upload, ChevronRight,
  FileText, Save, ShieldCheck, RefreshCw, Loader2, Download
} from "lucide-react";
import { STATUSES, STATUS_ORDER } from "@/lib/statuses";

const ICONS = { FileEdit, Send, CheckCircle2, BadgeCheck, AlertTriangle };

const LEVELS = ["6e", "5e", "4e", "3e", "2nde", "1ère", "Terminale"];
const OPTIONS = ["Latin", "Grec ancien", "Allemand LV2", "Espagnol LV2", "Arts plastiques", "Musique", "Sport études"];
const DOCS = [
  { key: "assurance_rc",         label: "Assurance scolaire / Responsabilité civile", hint: "Attestation d'assurance scolaire et extra-scolaire en cours de validité", required: true },
  { key: "certificat_naissance", label: "Certificat de naissance",                     hint: "Copie intégrale de l'acte de naissance (< 3 mois si possible)",                       required: true },
  { key: "certificat_radiation", label: "Certificat de radiation",                     hint: "Délivré par l'établissement précédent — facultatif si premier établissement",         required: false },
  { key: "bulletins",            label: "Bulletins scolaires (3 derniers)",            hint: "PDF regroupant les 3 derniers bulletins scolaires",                                   required: true },
  { key: "id_pere",              label: "Pièce d'identité — Père",                     hint: "CNI, passeport ou titre de séjour en cours de validité",                              required: true },
  { key: "id_mere",              label: "Pièce d'identité — Mère",                     hint: "CNI, passeport ou titre de séjour en cours de validité",                              required: true },
  { key: "id_enfant",            label: "Pièce d'identité — Enfant",                   hint: "CNI, passeport ou copie du titre de séjour",                                          required: true },
  { key: "vaccinations",         label: "Carnet de vaccination",                       hint: "Pages des vaccinations à jour (DTP, ROR, etc.)",                                      required: true },
  { key: "iban",                 label: "IBAN",                                        hint: "Relevé d'identité bancaire pour les prélèvements de scolarité",                       required: true },
  { key: "preuve_virement",      label: "Preuve de virement des frais d'inscription", hint: "Copie de l'avis de virement bancaire ou capture d'écran de l'ordre exécuté",          required: true },
  { key: "dossier_signe",        label: "Dossier d'inscription complet signé",         hint: "Formulaire d'inscription Lafayette signé par les deux parents",                       required: true },
];

function StatusTracker({ status }) {
  const currentIdx = STATUS_ORDER.indexOf(status === "rejected" ? "submitted" : status);
  return (
    <div className="flex items-center gap-2 sm:gap-4">
      {STATUS_ORDER.map((s, i) => {
        const meta = STATUSES[s];
        const Icon = ICONS[meta.icon] || FileEdit;
        const active = i <= currentIdx;
        return (
          <div key={s} className="flex items-center gap-2 sm:gap-4 flex-1">
            <div className="flex flex-col items-center text-center">
              <div
                data-testid={`tracker-step-${s}`}
                className="w-10 h-10 rounded-full grid place-items-center border-2 transition-all"
                style={{
                  borderColor: active ? meta.color : "#E2E8F0",
                  background: active ? meta.bg : "#fff",
                  color: active ? meta.color : "#94A3B8",
                }}
              >
                <Icon size={16}/>
              </div>
              <span className="text-[10px] tracking-[0.18em] uppercase mt-2 font-semibold" style={{ color: active ? meta.color : "#94A3B8" }}>
                {meta.label}
              </span>
            </div>
            {i < STATUS_ORDER.length - 1 && (
              <div className="flex-1 h-px" style={{ background: i < currentIdx ? STATUSES[STATUS_ORDER[i + 1]].color : "#E2E8F0" }}/>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StudentStep({ data, onChange }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div><label className="field-label">Prénom</label><input data-testid="f-firstname" className="field-input" value={data.firstname || ""} onChange={e => onChange({ ...data, firstname: e.target.value })}/></div>
      <div><label className="field-label">Nom</label><input data-testid="f-lastname" className="field-input" value={data.lastname || ""} onChange={e => onChange({ ...data, lastname: e.target.value })}/></div>
      <div><label className="field-label">Date de naissance</label><input data-testid="f-dob" type="date" className="field-input" value={data.date_of_birth || ""} onChange={e => onChange({ ...data, date_of_birth: e.target.value })}/></div>
      <div><label className="field-label">Lieu de naissance</label><input data-testid="f-pob" className="field-input" value={data.place_of_birth || ""} onChange={e => onChange({ ...data, place_of_birth: e.target.value })} placeholder="Nice, France"/></div>
      <div>
        <label className="field-label">Genre</label>
        <select data-testid="f-gender" className="field-input" value={data.gender || ""} onChange={e => onChange({ ...data, gender: e.target.value })}>
          <option value="">—</option><option value="F">Féminin</option><option value="M">Masculin</option><option value="X">Autre</option>
        </select>
      </div>
      <div><label className="field-label">Nationalité</label><input data-testid="f-nat" className="field-input" value={data.nationality || ""} onChange={e => onChange({ ...data, nationality: e.target.value })}/></div>
      <div><label className="field-label">École actuelle</label><input data-testid="f-school" className="field-input" value={data.current_school || ""} onChange={e => onChange({ ...data, current_school: e.target.value })}/></div>
      <div><label className="field-label">Classe actuelle</label><input data-testid="f-curlevel" className="field-input" value={data.current_level || ""} onChange={e => onChange({ ...data, current_level: e.target.value })}/></div>
      <div>
        <label className="field-label">Niveau demandé</label>
        <select data-testid="f-level" className="field-input" value={data.level_requested || ""} onChange={e => onChange({ ...data, level_requested: e.target.value })}>
          <option value="">—</option>
          {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className="field-label">Options souhaitées</label>
        <div className="flex flex-wrap gap-2 mt-1">
          {OPTIONS.map(opt => {
            const active = (data.options || []).includes(opt);
            return (
              <button
                type="button"
                key={opt}
                data-testid={`opt-${opt}`}
                onClick={() => {
                  const set = new Set(data.options || []);
                  if (active) set.delete(opt); else set.add(opt);
                  onChange({ ...data, options: Array.from(set) });
                }}
                className={`text-xs px-3 py-1.5 rounded-full border transition-all ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
              >{opt}</button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ParentsStep({ data, onChange }) {
  const set = (k) => (e) => onChange({ ...data, [k]: e.target.value });
  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-display font-semibold text-lg mb-3">Père</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="field-label">Nom complet</label><input data-testid="p-father-name" className="field-input" value={data.father_name || ""} onChange={set("father_name")}/></div>
          <div><label className="field-label">Téléphone</label><input data-testid="p-father-phone" className="field-input" value={data.father_phone || ""} onChange={set("father_phone")}/></div>
          <div><label className="field-label">Email</label><input data-testid="p-father-email" type="email" className="field-input" value={data.father_email || ""} onChange={set("father_email")}/></div>
          <div><label className="field-label">Profession</label><input data-testid="p-father-pro" className="field-input" value={data.father_profession || ""} onChange={set("father_profession")}/></div>
        </div>
      </div>
      <div>
        <h3 className="font-display font-semibold text-lg mb-3">Mère</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="field-label">Nom complet</label><input data-testid="p-mother-name" className="field-input" value={data.mother_name || ""} onChange={set("mother_name")}/></div>
          <div><label className="field-label">Téléphone</label><input data-testid="p-mother-phone" className="field-input" value={data.mother_phone || ""} onChange={set("mother_phone")}/></div>
          <div><label className="field-label">Email</label><input data-testid="p-mother-email" type="email" className="field-input" value={data.mother_email || ""} onChange={set("mother_email")}/></div>
          <div><label className="field-label">Profession</label><input data-testid="p-mother-pro" className="field-input" value={data.mother_profession || ""} onChange={set("mother_profession")}/></div>
        </div>
      </div>
      <div>
        <h3 className="font-display font-semibold text-lg mb-3">Adresse du foyer</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className="field-label">Adresse</label><input data-testid="p-address" className="field-input" value={data.address || ""} onChange={set("address")}/></div>
          <div><label className="field-label">Ville</label><input data-testid="p-city" className="field-input" value={data.city || ""} onChange={set("city")}/></div>
          <div><label className="field-label">Code postal</label><input data-testid="p-postal" className="field-input" value={data.postal_code || ""} onChange={set("postal_code")}/></div>
        </div>
      </div>
    </div>
  );
}

function DocumentsStep({ dossier, onUploaded }) {
  const [uploading, setUploading] = useState({});

  const handleUpload = async (key, file) => {
    setUploading(u => ({ ...u, [key]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/dossier/documents/${key}`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success(`${DOCS.find(d => d.key === key).label} téléversé`);
      onUploaded({ ...dossier, documents: { ...dossier.documents, [key]: data } });
    } catch (e) {
      toast.error(formatApiError(e, "Échec du téléversement"));
    } finally {
      setUploading(u => ({ ...u, [key]: false }));
    }
  };

  return (
    <div className="space-y-3">
      {DOCS.map(d => {
        const rec = dossier.documents?.[d.key];
        const isUp = uploading[d.key];
        const status = rec?.status;
        return (
          <div key={d.key} data-testid={`doc-row-${d.key}`} className={`upload-zone ${rec ? "has-file" : ""} flex items-center gap-4`}>
            <div className="w-10 h-10 rounded-md bg-slate-900 text-white grid place-items-center shrink-0">
              <FileText size={16}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{d.label}</span>
                {!d.required && (
                  <span className="text-[10px] tracking-[0.18em] uppercase font-semibold text-slate-400 px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50">Facultatif</span>
                )}
                {rec && (
                  <span className="status-pill" style={{ color: STATUSES[status]?.color, borderColor: STATUSES[status]?.color }}>
                    <span className="dot" style={{ background: STATUSES[status]?.color }}/>
                    {STATUSES[status]?.label || status}
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">{rec ? rec.original_filename : d.hint}</div>
              {rec?.admin_note && (
                <div className="text-xs text-red-600 mt-1">Note : {rec.admin_note}</div>
              )}
            </div>
            <label className="btn-ghost text-sm cursor-pointer">
              {(() => {
                if (isUp) return <Loader2 size={14} className="animate-spin"/>;
                if (rec) return <RefreshCw size={14}/>;
                return <Upload size={14}/>;
              })()}
              {rec ? "Remplacer" : "Téléverser"}
              <input
                data-testid={`doc-input-${d.key}`}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => e.target.files?.[0] && handleUpload(d.key, e.target.files[0])}
              />
            </label>
          </div>
        );
      })}
    </div>
  );
}

function FinalStatusPanel({ dossier }) {
  if (dossier.status === "validated") {
    const downloadCertificate = async () => {
      try {
        const res = await api.get("/dossier/certificate", { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        const last = (dossier?.student?.lastname || "eleve").toUpperCase().replace(/\s+/g, "_");
        const first = (dossier?.student?.firstname || "").replace(/\s+/g, "_");
        a.download = first ? `Certificat_inscription_${last}_${first}.pdf` : `Certificat_inscription_${last}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        toast.error(formatApiError(e, "Téléchargement du certificat impossible"));
      }
    };
    return (
      <div className="card p-6 border-emerald-200 bg-emerald-50" data-testid="dossier-final-validated">
        <div className="flex items-start gap-3">
          <BadgeCheck className="text-emerald-700 shrink-0 mt-0.5" size={20}/>
          <div className="min-w-0 flex-1">
            <div className="font-display font-semibold text-lg">Inscription confirmée</div>
            <p className="text-sm text-slate-700 mt-1">
              Votre dossier a été entièrement validé par l'administration de Lafayette International School.
              Vous recevrez prochainement par email la convocation et les informations de rentrée.
            </p>
            <button
              data-testid="download-certificate-btn"
              type="button"
              onClick={downloadCertificate}
              className="btn-cta mt-4 text-sm inline-flex"
            >
              <Download size={14}/> Télécharger mon certificat d'inscription
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="card p-6" data-testid="dossier-final-pending">
      <div className="flex items-start gap-3">
        <ShieldCheck className="text-slate-400 mt-1" size={20}/>
        <div>
          <div className="font-display font-semibold text-lg">Validation administrative</div>
          <p className="text-sm text-slate-600 mt-1">
            Une fois votre dossier soumis avec toutes les pièces (y compris la preuve de virement des frais d'inscription),
            le secrétariat de Lafayette vérifie chaque pièce. Vous recevrez la confirmation d'inscription dès la validation finale.
          </p>
        </div>
      </div>
    </div>
  );
}

const STEPS = [
  { key: "student", label: "Informations élève" },
  { key: "parents", label: "Parents & foyer" },
  { key: "documents", label: "Pièces justificatives" },
  { key: "review", label: "Vérification & soumission" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [dossier, setDossier] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/dossier");
    setDossier(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!dossier) return <div className="p-10 overline text-slate-500">Chargement du dossier…</div>;

  const locked = !["draft", "rejected"].includes(dossier.status);

  const saveStudent = async (student) => {
    setSaving(true);
    try {
      const { data } = await api.put("/dossier", { student });
      setDossier(data);
    } finally { setSaving(false); }
  };
  const saveParents = async (parents) => {
    setSaving(true);
    try {
      const { data } = await api.put("/dossier", { parents });
      setDossier(data);
    } finally { setSaving(false); }
  };

  const submit = async () => {
    try {
      const { data } = await api.post("/dossier/submit");
      setDossier(data);
      toast.success("Dossier soumis pour validation");
      // Fire-and-forget notification email to the school (best effort).
      notifyAdminOfDossierSubmission(user, data).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e, "Soumission impossible"));
    }
  };

  const missing = (() => {
    const s = dossier.student || {};
    const p = dossier.parents || {};
    const mStudent = ["firstname", "lastname", "date_of_birth", "level_requested"].filter(k => !s[k]);
    const mParents = ["father_name", "father_phone", "mother_name", "address", "city"].filter(k => !p[k]);
    const mDocs = DOCS.filter(d => d.required && !dossier.documents?.[d.key]).map(d => d.label);
    return { mStudent, mParents, mDocs };
  })();
  const canSubmit = !missing.mStudent.length && !missing.mParents.length && !missing.mDocs.length;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Espace parent</span>
          <h1 className="font-display text-4xl font-bold mt-1">Bonjour, {user.name.split(" ")[0]}</h1>
          <p className="text-sm text-slate-600 mt-1">Suivez l'état du dossier d'inscription et complétez les étapes manquantes.</p>
        </div>
        <div className="status-pill" data-testid="dossier-status-pill" style={{ color: STATUSES[dossier.status]?.color, borderColor: STATUSES[dossier.status]?.color }}>
          <span className="dot" style={{ background: STATUSES[dossier.status]?.color }}/>
          Dossier {STATUSES[dossier.status]?.label}
        </div>
      </div>

      <div className="card p-6 mt-6">
        <StatusTracker status={dossier.status}/>
      </div>

      {dossier.admin_notes && dossier.status === "rejected" && (
        <div className="card p-4 mt-4 border-red-200 bg-red-50">
          <div className="flex gap-2 items-start text-sm text-red-700">
            <AlertTriangle size={16} className="mt-0.5"/> <div><b>Notes de l'administration :</b> {dossier.admin_notes}</div>
          </div>
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-12 gap-6">
        {/* Step nav */}
        <aside className="lg:col-span-3">
          <div className="card p-2 sticky top-24">
            {STEPS.map((s, i) => (
              <button
                key={s.key}
                data-testid={`step-nav-${s.key}`}
                onClick={() => setStep(i)}
                className={`w-full text-left px-3 py-2.5 rounded-md flex items-center gap-3 text-sm transition-all ${step === i ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-700"}`}
              >
                <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold ${step === i ? "bg-white text-slate-900" : "bg-slate-200 text-slate-700"}`}>{i + 1}</span>
                {s.label}
              </button>
            ))}
          </div>
        </aside>

        {/* Step content */}
        <section className="lg:col-span-9">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl font-bold">{STEPS[step].label}</h2>
              {saving && <span className="text-xs text-slate-400 flex items-center gap-1"><Loader2 size={12} className="animate-spin"/> Enregistrement…</span>}
            </div>

            {step === 0 && (
              <>
                <StudentStep data={dossier.student || {}} onChange={(s) => { setDossier(d => ({ ...d, student: s })); }}/>
                <div className="flex justify-end mt-6">
                  <button data-testid="save-student-btn" disabled={locked} onClick={() => saveStudent(dossier.student)} className="btn-primary">
                    <Save size={14}/> Enregistrer
                  </button>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <ParentsStep data={dossier.parents || {}} onChange={(p) => setDossier(d => ({ ...d, parents: p }))}/>
                <div className="flex justify-end mt-6">
                  <button data-testid="save-parents-btn" disabled={locked} onClick={() => saveParents(dossier.parents)} className="btn-primary">
                    <Save size={14}/> Enregistrer
                  </button>
                </div>
              </>
            )}

            {step === 2 && (
              <DocumentsStep dossier={dossier} onUploaded={setDossier}/>
            )}

            {step === 3 && (
              <div>
                <p className="text-sm text-slate-600">Vérifiez que toutes les étapes sont complètes avant de soumettre votre dossier pour validation.</p>
                <ul className="mt-5 space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    {missing.mStudent.length === 0 ? <CheckCircle2 size={16} className="text-emerald-600"/> : <AlertTriangle size={16} className="text-amber-600"/>}
                    Informations élève {missing.mStudent.length ? `— manquant : ${missing.mStudent.join(", ")}` : "complètes"}
                  </li>
                  <li className="flex items-center gap-2">
                    {missing.mParents.length === 0 ? <CheckCircle2 size={16} className="text-emerald-600"/> : <AlertTriangle size={16} className="text-amber-600"/>}
                    Parents & foyer {missing.mParents.length ? `— manquant : ${missing.mParents.join(", ")}` : "complets"}
                  </li>
                  <li className="flex items-center gap-2">
                    {missing.mDocs.length === 0 ? <CheckCircle2 size={16} className="text-emerald-600"/> : <AlertTriangle size={16} className="text-amber-600"/>}
                    Pièces justificatives {missing.mDocs.length ? `— manquant : ${missing.mDocs.join(", ")}` : "complètes"}
                  </li>
                </ul>

                <div className="flex flex-wrap gap-3 items-center mt-6">
                  <button
                    data-testid="submit-dossier-btn"
                    onClick={submit}
                    disabled={!canSubmit || locked}
                    className="btn-primary"
                  >
                    <Send size={14}/> Soumettre pour validation
                  </button>
                  {locked && <span className="text-xs text-slate-500">Dossier déjà soumis — modification impossible.</span>}
                </div>

                <div className="mt-8">
                  <FinalStatusPanel dossier={dossier}/>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between mt-4">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="btn-ghost text-sm">
              Précédent
            </button>
            <button onClick={() => setStep(Math.min(STEPS.length - 1, step + 1))} disabled={step === STEPS.length - 1} className="btn-primary text-sm">
              Suivant <ChevronRight size={14}/>
            </button>
          </div>

          <div className="mt-8 flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck size={14}/> Vos données sont chiffrées et accessibles uniquement par l'administration de l'établissement.
          </div>
        </section>
      </div>
    </div>
  );
}
